import React, { useEffect, useState } from "react";
import { PlugZap } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/** Ford wallbox (home charge station) health — /v1/wallbox via backend.
 *  Renders only for connected Ford sessions that have a wallbox. */
export const FordWallboxCard = ({ vehicle }) => {
  const [data, setData] = useState(null);
  const sessionId =
    typeof window !== "undefined" ? window.localStorage.getItem("eviq.tesla_session_id") : null;
  const isFordSession = !!sessionId && sessionId.startsWith("ford-");

  useEffect(() => {
    if (!isFordSession || !API) return;
    fetch(`${API}/api/ford/wallbox?session_id=${encodeURIComponent(sessionId)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setData)
      .catch(() => setData({ wallboxes: [] }));
  }, [sessionId, isFordSession]);

  if (vehicle?.brand !== "ford" || !isFordSession) return null;
  const boxes = data?.wallboxes || [];
  if (data !== null && boxes.length === 0) return null;

  return (
    <div
      className="rounded-xl border p-4"
      style={{ borderColor: "rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)" }}
      data-testid="ford-wallbox-card"
    >
      <div className="flex items-center gap-3">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center border shrink-0"
          style={{ background: "rgba(57,255,20,0.08)", borderColor: "rgba(57,255,20,0.30)" }}
        >
          <PlugZap className="w-4 h-4" style={{ color: "#39FF14" }} strokeWidth={2.4} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-500">Home charging</div>
          <div className="text-sm text-white font-semibold leading-snug">
            {data === null ? "Checking your charge station…" : boxes[0].name}
          </div>
        </div>
        {boxes[0] && (
          <span
            className={`shrink-0 font-mono text-[8px] uppercase tracking-[0.14em] rounded-full px-1.5 py-0.5 border ${
              boxes[0].status === "CONNECTED"
                ? "border-[#39FF14]/50 text-[#39FF14] bg-[#39FF14]/[0.10]"
                : "border-white/20 text-zinc-400 bg-white/[0.04]"
            }`}
            data-testid="ford-wallbox-status"
          >
            {boxes[0].status}
          </span>
        )}
      </div>

      {boxes[0] && (
        <div className="mt-3 grid grid-cols-3 gap-2" data-testid="ford-wallbox-stats">
          <div className="rounded-lg border border-white/10 bg-black/20 p-2.5">
            <div className="font-mono text-[8px] uppercase tracking-wider text-zinc-500">Last session</div>
            <div className="text-[13px] text-white font-semibold mt-0.5">
              {typeof boxes[0].last_session_kwh === "number" ? `${boxes[0].last_session_kwh} kWh` : "—"}
            </div>
          </div>
          <div className="rounded-lg border border-white/10 bg-black/20 p-2.5">
            <div className="font-mono text-[8px] uppercase tracking-wider text-zinc-500">Duration</div>
            <div className="text-[13px] text-white font-semibold mt-0.5">
              {typeof boxes[0].last_session_minutes === "number"
                ? `${Math.floor(boxes[0].last_session_minutes / 60)}h ${boxes[0].last_session_minutes % 60}m`
                : "—"}
            </div>
          </div>
          <div className="rounded-lg border border-white/10 bg-black/20 p-2.5">
            <div className="font-mono text-[8px] uppercase tracking-wider text-zinc-500">Lifetime</div>
            <div className="text-[13px] text-white font-semibold mt-0.5">
              {typeof boxes[0].total_kwh === "number" ? `${Math.round(boxes[0].total_kwh).toLocaleString()} kWh` : "—"}
            </div>
          </div>
        </div>
      )}

      {data?.demo && (
        <p className="font-mono text-[9px] text-zinc-600 mt-2 uppercase tracking-wider">
          Demo data — sample charge station for preview
        </p>
      )}
    </div>
  );
};
